import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, UserRound } from "lucide-react";
import { NAV } from "../../lib/site";
import { MyTicketsDialog } from "./MyTicketsDialog";
import { EASE } from "./Motion";
import { useAuth, homeFor } from "../../lib/auth";

const useAccount = () => {
  const { user } = useAuth();
  if (!user) return { to: "/cuenta", label: "Ingresar" };
  return { to: user.role === "pasajero" ? "/cuenta" : homeFor(user.role), label: user.role === "pasajero" ? (user.name ?? "Mi cuenta").split(" ")[0] : "Panel" };
};

const DesktopNav = () => (
  <nav className="hidden items-center gap-1 lg:flex">
    {NAV.map((n) => (
      <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.label.toLowerCase()}`} className="relative px-3 py-2 text-[15px] font-semibold text-[var(--ink)] xl:px-4">
        {({ isActive }) => (
          <>
            <span className="relative z-10 transition-colors duration-300 hover:text-[var(--forest)]">{n.label}</span>
            {isActive && <motion.span layoutId="nav-pill" className="absolute inset-x-3 -bottom-0.5 h-[3px] rounded-full bg-[var(--forest)]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
          </>
        )}
      </NavLink>
    ))}
  </nav>
);

const MobileMenu = ({ open, onClose }) => {
  const acc = useAccount();
  return createPortal(
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid="mobile-menu"
        initial={{ clipPath: "circle(0% at 100% 0%)" }}
        animate={{ clipPath: "circle(150% at 100% 0%)" }}
        exit={{ clipPath: "circle(0% at 100% 0%)" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="fixed inset-0 z-[70] flex flex-col bg-[var(--night)] px-6 pb-10 pt-24 lg:hidden"
      >
        <button onClick={onClose} data-testid="mobile-menu-close" aria-label="Cerrar menú" className="absolute right-5 top-5 rounded-full border border-white/20 p-3 text-white">
          <X className="h-5 w-5" />
        </button>
        {NAV.map((n, i) => (
          <motion.div key={n.to} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.06, ease: EASE, duration: 0.6 }}>
            <Link to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="block border-b border-white/10 py-4 font-display text-4xl text-white">
              {n.label}
            </Link>
          </motion.div>
        ))}
        <div className="mt-auto space-y-3"><Link to={acc.to} onClick={onClose} data-testid="mobile-account-link" className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--lime)] px-5 py-2.5 text-sm font-bold text-[var(--ink)]"><UserRound className="h-4 w-4" /> {acc.label}</Link><MyTicketsDialog dark onOpenChange={() => {}} /></div>
      </motion.div>
    )}
  </AnimatePresence>,
  document.body
  );
};

export const Header = () => {
  const acc = useAccount();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header data-testid="site-header" className={`sticky top-0 z-40 transition-[background-color,box-shadow] duration-500 ${scrolled ? "bg-[var(--paper)]/85 shadow-[0_1px_0_rgba(11,17,13,.08)] backdrop-blur-xl" : "bg-[var(--paper)]"}`}>
      <div className="mx-auto flex h-[84px] max-w-[1400px] items-center justify-between gap-4 px-5 md:h-[104px] md:px-10">
        <Link to="/" data-testid="header-logo-link" aria-label="Santiagueño — Inicio" className="shrink-0">
          <img src="/brand/logo-green.png" alt="Santiagueño" className="h-12 w-auto md:h-14 xl:h-16" />
        </Link>
        <DesktopNav />
        <div className="flex items-center gap-3">
          <Link to={acc.to} data-testid="header-account-link" aria-label={acc.label} title={acc.label} className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] p-3 text-sm font-bold text-white transition-colors hover:bg-[var(--forest)] xl:px-5 xl:py-2.5"><UserRound className="h-4 w-4" /><span className="hidden xl:inline">{acc.label}</span></Link>
          <div className="hidden lg:block"><MyTicketsDialog /></div>
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Abrir menú" className="rounded-full border border-[var(--ink)]/15 p-3 lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </header>
  );
};
